<script lang="ts">
  import { CheckCircle2, Clock, DollarSign } from "@lucide/svelte"

  import TaskStatusBadge from "@/components/task/task-status-badge.svelte"
  import { Button } from "@/components/ui/button"
  import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
  } from "@/components/ui/card"
  import type { TaskInstance } from "@/types"

  interface Props {
    taskInstance: TaskInstance
    showActions?: boolean
    onComplete?: () => void
    onApprove?: () => void
    onClaim?: () => void
    onCancel?: () => void
    compactDate?: boolean
  }

  let {
    taskInstance,
    showActions = false,
    onComplete,
    onApprove,
    onClaim,
    onCancel,
    compactDate = false,
  }: Props = $props()

  const status = $derived(getStatus(taskInstance))
  const isRejected = $derived(
    taskInstance.feedback && taskInstance.feedback.trim() !== "",
  )
  const canComplete = $derived(
    (status === "pending" || isRejected) && showActions && !!onComplete,
  )
  const canApprove = $derived(
    status === "pending_approval" && showActions && !!onApprove,
  )
  const canClaim = $derived(status === "pending" && showActions && !!onClaim)

  function getStatus(
    taskInstance: TaskInstance,
  ):
    | "pending"
    | "completed"
    | "pending_approval"
    | "approved"
    | "paid"
    | "rejected" {
    if (taskInstance.feedback && taskInstance.feedback.trim() !== "")
      return "rejected"
    if (taskInstance.paid) return "paid"
    if (taskInstance.approved_at) return "approved"
    if (
      taskInstance.completed_at &&
      taskInstance.task_template?.requires_approval
    ) {
      return "pending_approval"
    }
    if (taskInstance.completed_at) return "completed"
    return "pending"
  }

  function formatDate(dateString?: string): string {
    if (!dateString) return ""
    const date = new Date(dateString)
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
  }

  function formatTime(dateString?: string): string {
    if (!dateString) return ""
    const date = new Date(dateString)
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    })
  }

  function handleComplete() {
    if (onComplete) {
      onComplete()
    }
  }

  function handleApprove() {
    if (onApprove) {
      onApprove()
    }
  }

  function handleClaim() {
    if (onClaim) {
      onClaim()
    }
  }

  function handleCancel() {
    if (onCancel) {
      onCancel()
    }
  }
</script>

<Card>
  <CardHeader>
    <div class="flex items-start justify-between">
      <div class="flex-1">
        <CardTitle>{taskInstance.task_template?.name || "Task"}</CardTitle>
        {#if taskInstance.task_template?.description}
          <CardDescription class="mt-1"
            >{taskInstance.task_template.description}</CardDescription
          >
        {/if}
      </div>
      <TaskStatusBadge {status} />
    </div>
  </CardHeader>
  <CardContent class="space-y-3">
    {#if taskInstance.feedback && taskInstance.feedback.trim() !== ""}
      <div
        class="border-destructive/50 bg-destructive/10 rounded-md border p-3"
      >
        <div class="text-sm">{taskInstance.feedback}</div>
      </div>
    {/if}
    {#if taskInstance.assigned_to && taskInstance.completed_at && !compactDate}
      <div class="text-muted-foreground flex items-center gap-2 text-sm">
        <CheckCircle2 class="h-4 w-4" />
        <span>Completed by {taskInstance.assigned_to.name}</span>
      </div>
    {/if}
    {#if taskInstance.completed_at}
      <div class="text-muted-foreground flex items-center gap-2 text-sm">
        <Clock class="h-4 w-4" />
        {#if compactDate}
          <span
            >on {formatDate(taskInstance.completed_at)} at {formatTime(
              taskInstance.completed_at,
            ).toLowerCase()}</span
          >
        {:else}
          <span
            >Completed {formatDate(taskInstance.completed_at)} at {formatTime(
              taskInstance.completed_at,
            )}</span
          >
        {/if}
      </div>
    {/if}
    {#if taskInstance.approved_by}
      <div class="text-muted-foreground flex items-center gap-2 text-sm">
        <CheckCircle2 class="h-4 w-4" />
        <span>Approved by {taskInstance.approved_by.name}</span>
      </div>
    {/if}
    {#if taskInstance.task_template?.wage}
      <div class="flex items-center gap-2 text-sm font-medium">
        <DollarSign class="h-4 w-4" />
        <span
          >{Number.parseFloat(taskInstance.task_template.wage).toFixed(2)}</span
        >
      </div>
    {/if}
  </CardContent>
  {#if showActions && (canComplete || canApprove || canClaim || onCancel)}
    <CardFooter class="gap-2">
      {#if canComplete}
        <Button onclick={handleComplete} class="flex-1">Complete</Button>
      {/if}
      {#if canClaim}
        <Button onclick={handleClaim} class="flex-1">Claim</Button>
      {/if}
      {#if canApprove}
        <Button onclick={handleApprove} class="flex-1">Approve</Button>
      {/if}
      {#if onCancel}
        <Button onclick={handleCancel} variant="destructive" class="flex-1"
          >Cancel</Button
        >
      {/if}
    </CardFooter>
  {/if}
</Card>
